import { useState, useEffect } from "react";
import { Menu, X, Shield } from "lucide-react";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Experience", href: "#experience" },
  { label: "Expertise", href: "#expertise" },
  { label: "Services", href: "#services" },
  { label: "Impact", href: "#impact" },
  { label: "Blog", href: "#blog" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      const sections = navLinks.map(l => l.href.slice(1));
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav border-b border-[#E5E7EB] shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <button onClick={() => handleClick("#home")} className="flex items-center gap-2 group" data-testid="nav-logo">
            <Shield className="w-6 h-6 text-[#06B6D4] transition-transform group-hover:scale-110" />
            <div className="text-left">
              <span className="font-['Outfit'] font-bold text-[#0F172A] text-lg leading-none">Piyus Kumar</span>
              <span className="block text-[10px] font-medium text-[#6B7280] tracking-wider uppercase">ShiftLeft Security</span>
            </div>
          </button>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleClick(link.href)}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeSection === link.href.slice(1)
                    ? "text-[#06B6D4] bg-[#06B6D4]/5"
                    : "text-[#6B7280] hover:text-[#111827] hover:bg-[#F9FAFB]"
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden lg:block">
            <button
              onClick={() => handleClick("#contact")}
              data-testid="nav-cta-button"
              className="inline-flex items-center px-5 py-2.5 rounded-lg bg-[#0F172A] text-white text-sm font-medium transition-all hover:bg-[#111827] hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]"
            >
              Work With Me
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-lg text-[#6B7280] hover:bg-[#F9FAFB]"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#E5E7EB] shadow-lg animate-fadeIn" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleClick(link.href)}
                className={`block w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                  activeSection === link.href.slice(1)
                    ? "text-[#06B6D4] bg-[#06B6D4]/5"
                    : "text-[#6B7280] hover:text-[#111827] hover:bg-[#F9FAFB]"
                }`}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => handleClick("#contact")}
              className="w-full mt-2 px-5 py-2.5 rounded-lg bg-[#0F172A] text-white text-sm font-medium"
              data-testid="mobile-cta-button"
            >
              Work With Me
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
